import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichText } from "@/components/RichText";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";

function SectionLanguageSwitch({ value, onChange, sectionIndex }) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-muted/40 p-1" role="group" aria-label={`Section ${sectionIndex + 1} language`}>
      {[
        { value: "id", label: "Indonesia" },
        { value: "en", label: "English" },
      ].map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          aria-label={`Edit Section ${sectionIndex + 1} ${option.label} content`}
          data-testid={`sec-lang-${option.value}-${sectionIndex}`}
          onClick={() => onChange(option.value)}
          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${value === option.value ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function SectionCard({ section, index, total, updSection, moveSection, rmSection, lang }) {
  const [activeLanguage, setActiveLanguage] = useState("id");
  const languageName = activeLanguage === "id" ? "Indonesia" : "English";
  const titleKey = `title_${activeLanguage}`;
  const descriptionKey = `description_${activeLanguage}`;

  return (
    <article className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm" data-testid={`sec-row-${index}`}>
      <header className="flex flex-wrap items-center gap-3 border-b border-border bg-muted/20 px-4 py-3 sm:px-5">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {lang === "id" ? "Bagian" : "Section"} {section.order || index + 1}
          </div>
          <div className="mt-0.5 text-sm font-medium text-foreground">
            {section[`title_${activeLanguage}`] || section.title_id || section.title_en || (lang === "id" ? "Bagian tanpa judul" : "Untitled Section")}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={`Move Section ${index + 1} up`}
            title="Move Section up"
            disabled={index === 0}
            onClick={() => moveSection(index, -1)}
            data-testid={`sec-move-up-${index}`}
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={`Move Section ${index + 1} down`}
            title="Move Section down"
            disabled={index === total - 1}
            onClick={() => moveSection(index, 1)}
            data-testid={`sec-move-down-${index}`}
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={`Delete Section ${index + 1}`}
            title="Delete Section"
            className="text-destructive hover:text-destructive"
            onClick={() => rmSection(index)}
            data-testid={`sec-remove-${index}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div className="space-y-4 p-4 sm:p-5">
        <SectionLanguageSwitch value={activeLanguage} onChange={setActiveLanguage} sectionIndex={index} />
        <div className="space-y-1.5">
          <Label htmlFor={`section-title-${section.id}-${activeLanguage}`} className="text-xs uppercase tracking-wider text-muted-foreground">
            {lang === "id" ? "Judul Bagian" : "Section title"} · {languageName}
          </Label>
          <Input
            id={`section-title-${section.id}-${activeLanguage}`}
            aria-label={`Section ${index + 1} title in ${languageName}`}
            value={section[titleKey] || ""}
            onChange={(event) => updSection(index, { [titleKey]: event.target.value })}
            data-testid={`sec-title-${activeLanguage}-${index}`}
            className="h-11 text-base"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            {lang === "id" ? "Deskripsi" : "Description"} · {languageName}
          </Label>
          <RichText
            value={section[descriptionKey] || ""}
            onChange={(value) => updSection(index, { [descriptionKey]: value })}
            mode="description"
            minHeight={90}
            placeholder={lang === "id" ? "Tambahkan konteks opsional untuk bagian ini…" : "Add optional context for this Section…"}
            ariaLabel={`Section ${index + 1} description in ${languageName}`}
            testid={`sec-desc-${activeLanguage}-${index}`}
          />
        </div>
      </div>
    </article>
  );
}

export default function SectionsTab({ inst, addSection, updSection, moveSection, rmSection, lang }) {
  return (
    <div className="space-y-4">
      {inst.sections.map((section, index) => (
        <SectionCard
          key={section.id}
          section={section}
          index={index}
          total={inst.sections.length}
          updSection={updSection}
          moveSection={moveSection}
          rmSection={rmSection}
          lang={lang}
        />
      ))}
      {inst.sections.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-muted/10 px-5 py-8 text-center text-sm text-muted-foreground">
          {lang === "id" ? "Belum ada Bagian. Tambahkan Bagian untuk mengelompokkan pertanyaan secara visual." : "No Sections yet. Add one to group questions visually."}
        </div>
      )}
      <Button onClick={addSection} variant="outline" className="rounded-full" data-testid="sec-add">
        <Plus className="mr-1 h-4 w-4" /> {lang === "id" ? "Tambah Bagian" : "Add Section"}
      </Button>
    </div>
  );
}
